import React, { useState } from 'react';
import useGetListLogContainerApp from 'redux/slices/containerApp/hooks/useGetListLogContainerApp';
import { useParams } from 'react-router-dom';
import { Box, Pagination, Switch, FormControlLabel } from '@mui/material';
import { useSelector } from 'react-redux';
import { inputFormat } from '@tenant/utility/constants/default';
import { DateMenu } from '@tenant/core/App/Date/DateMenu';
import { getRangeDateByKeyName } from '@tenant/utility/helper/Utils';
import useNextTick from '@tenant/utility/hooks/useNextTick';
import moment from 'moment';
import { STATISTIC_DATE_GROUP_TYPE } from '@tenant/utility/constants/enum';
import LogList from 'pages/containerApp/Common/LogList';
import {
  DEFAULT_PAGE_INDEX,
  DEFAULT_PAGE_SIZE,
} from '@tenant/utility/constants/default';
import FooterWrapper from '@tenant/core/AppLayout/components/AppFooter/FooterWrapper';

const defaultRangeName = 'Last 1 day';
const dataOptions = {
  'Last 1 day': {
    value: [
      moment().subtract(1, 'days').format(inputFormat),
      moment().format(inputFormat),
    ],
    description: `${moment().subtract(1, 'days').format('MMM')} ${moment()
      .subtract(1, 'days')
      .format('DD')}-${moment().format('MMM')} ${moment().format('DD')}`,
    key: 'days',
    groupType: STATISTIC_DATE_GROUP_TYPE.Day,
    title: 'Relative dates',
    text: 'Last 1 day',
  },
  'Last 3 days': {
    value: [
      moment().subtract(3, 'days').format(inputFormat),
      moment().format(inputFormat),
    ],
    description: `${moment().subtract(3, 'days').format('MMM')} ${moment()
      .subtract(3, 'days')
      .format('DD')}-${moment().format('MMM')} ${moment().format('DD')}`,
    key: 'days',
    groupType: STATISTIC_DATE_GROUP_TYPE.Day,
    title: 'Relative dates',
    text: 'Last 3 days',
  },
  'Last 7 days': {
    value: [
      moment().subtract(7, 'days').format(inputFormat),
      moment().format(inputFormat),
    ],
    description: `${moment().subtract(7, 'days').format('MMM')} ${moment()
      .subtract(7, 'days')
      .format('DD')}-${moment().format('MMM')} ${moment().format('DD')}`,
    key: 'days',
    groupType: STATISTIC_DATE_GROUP_TYPE.Day,
    title: 'Relative dates',
    text: 'Last 7 days',
  },
  'Last 30 days': {
    value: [
      moment().subtract(30, 'days').format(inputFormat),
      moment().format(inputFormat),
    ],
    description: `${moment().subtract(30, 'days').format('MMM')} ${moment()
      .subtract(30, 'days')
      .format('DD')}-${moment().format('MMM')} ${moment().format('DD')}`,
    key: 'days',
    groupType: STATISTIC_DATE_GROUP_TYPE.Day,
    title: 'Relative dates',
    text: 'Last 30 days',
  },
};
const { start, end } = getRangeDateByKeyName(defaultRangeName, dataOptions);

const Logs = () => {
  const nextTick = useNextTick();
  const { log, logFilter } = useSelector((s) => s.containerApp);
  const [rangeDate, setRangeDate] = useState(defaultRangeName);

  const [filter, setFilter] = useState(logFilter ? { ...logFilter } : {});
  const [pageIndex, setPageIndex] = useState(
    logFilter?.pageIndex ?? DEFAULT_PAGE_INDEX,
  );

  const { appInstanceId, id: containerAppId } = useParams();
  const { isLoading, isRefetching, refetch } = useGetListLogContainerApp({
    startDate: start,
    endDate: end,
    ...filter,
    appInstanceId,
    containerAppId,
    currentPage: pageIndex,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const handleChangeDate = async (data) => {
    const { keyName, startDate, endDate } = data || {};
    setRangeDate(keyName);
    setFilter({
      startDate,
      endDate,
    });
    await nextTick();
    refetch();
  };

  const handleChangeIncludeErrorOnly = async (e) => {
    const includeErrorsOnly = e.target.checked;
    setFilter((prev) => ({
      ...prev,
      includeErrorsOnly,
    }));
    await nextTick();
    refetch();
  }

  const onPageChange = (value) => {
    setPageIndex(value);
  };

  return (
    <>
      <Box display={'flex'} justifyContent={'space-between'}>
        <FormControlLabel
          control={
            <Switch
              checked={logFilter?.includeErrorsOnly ?? false}
              onChange={(e) => {
                handleChangeIncludeErrorOnly(e);
              }}
            />
          }
          label='Error Logs Only'
        />

        <DateMenu
          defaultValue={rangeDate}
          handleChangeValue={(data) => handleChangeDate(data)}
          dataOptions={dataOptions}
          isCustomDateRange={false}
        />
      </Box>

      <LogList data={log?.Data ?? []} loading={isLoading || isRefetching} />

      <FooterWrapper className='footer'>
        <Box justifyContent={'center'} className='footerContainer'>
          <Pagination
            count={Math.ceil(log.TotalCount / DEFAULT_PAGE_SIZE) || 1}
            page={pageIndex}
            onChange={(e, v) => onPageChange(v)}
            showFirstButton
            showLastButton
          />
        </Box>
      </FooterWrapper>
    </>
  );
};

export default React.memo(Logs);

Logs.propTypes = {};
